'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { getLodgesForActivity, Lodge } from '@/lib/data/lodges';
import { listUnits } from '@/lib/admin/units-service';
import type { Unit } from '@/lib/admin/units-service';
import { useUnifiedCart } from '@/lib/unified-cart';
import { useQuote } from '@/lib/hooks/useQuote';
import type { QuoteResponse } from '@/lib/services/guesty-booking';
import BookingCalendar from '@/components/booking/BookingCalendar';
import styles from './LodgeCrossSell.module.css';

/* ──────────────────────────────────────────────
   TYPES
   ────────────────────────────────────────────── */

interface LodgeCrossSellProps {
  activityId: string;
  locale: 'en' | 'es' | 'pt';
  onSkip?: () => void;
  className?: string;
}

/** Unified item for the cross-sell carousel */
interface CrossSellCard {
  id: string;              // unit.id for bookable, lodge.id for comingSoon
  name: string;
  image: string;
  locationName: string;
  statLine: string;
  isBookable: boolean;
  comingSoonLabel?: string;
  unit?: Unit;             // Real Firestore unit (bookable items only)
  lodge: Lodge;            // Parent marketing lodge (always present)
}

/* ──────────────────────────────────────────────
   I18N — migrated to next-intl (namespace: lodgeCrossSell)
   ────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   INLINE SVG ICONS
   ────────────────────────────────────────────── */

function CheckIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ──────────────────────────────────────────────
   DEBOUNCE CONSTANT
   ────────────────────────────────────────────── */
const PRICE_FETCH_DEBOUNCE_MS = 400;

/* ──────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────── */

export default function LodgeCrossSell({
  activityId,
  locale,
  onSkip,
  className,
}: LodgeCrossSellProps) {
  const t = useTranslations('lodgeCrossSell');
  const { addLodgeItem, lodgeItems } = useUnifiedCart();

  // --- Marketing lodges for this activity (provides location mapping) ---
  const relevantLodges = useMemo(() => getLodgesForActivity(activityId), [activityId]);

  // --- Fetch real bookable units from Firestore API ---
  const [allUnits, setAllUnits] = useState<Unit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchUnits() {
      try {
        const units = await listUnits({ active: 'true', sortBy: 'sortOrder', sortDir: 'asc' });
        if (!cancelled) setAllUnits(units);
      } catch (err) {
        console.error('Failed to fetch units for cross-sell:', err);
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    }
    fetchUnits();
    return () => { cancelled = true; };
  }, []);

  // --- Build combined card items: real units + non-bookable lodges ---
  const items = useMemo((): CrossSellCard[] => {
    if (unitsLoading) return [];

    const locationIds = new Set(relevantLodges.map(l => l.id));
    const relevantUnits = allUnits.filter(u => locationIds.has(u.locationId));
    const result: CrossSellCard[] = [];

    // 1. Real bookable unit cards
    for (const unit of relevantUnits) {
      const parentLodge = relevantLodges.find(l => l.id === unit.locationId);
      if (!parentLodge) continue;

      const statParts: string[] = [];
      if (unit.maxOccupancy) statParts.push(`${unit.maxOccupancy} ${t('guests')}`);
      if (unit.bedConfiguration) statParts.push(unit.bedConfiguration);

      result.push({
        id: unit.id,
        name: unit.name,
        image: unit.images?.[0] || parentLodge.heroImage,
        locationName: parentLodge.location.split('•')[0].trim(),
        statLine: statParts.join(' | '),
        isBookable: true,
        unit,
        lodge: parentLodge,
      });
    }

    // 2. Non-bookable lodge cards — comingSoon or soldOut (only for lodges with NO units)
    const locationIdsWithUnits = new Set(relevantUnits.map(u => u.locationId));
    for (const lodge of relevantLodges) {
      if (locationIdsWithUnits.has(lodge.id)) continue;
      if (!lodge.comingSoon && !lodge.soldOut) continue;

      const guestStat = lodge.stats.find(s => s.label === 'Guests');
      const extraStat = lodge.stats.find(s =>
        s.label === 'Elevation' || s.label === 'Hectares' || s.label === 'Private River'
      );
      const parts: string[] = [];
      if (guestStat) parts.push(`${guestStat.number} ${guestStat.label}`);
      if (extraStat) parts.push(`${extraStat.number} ${extraStat.label}`);

      result.push({
        id: lodge.id,
        name: lodge.name,
        image: lodge.heroImage,
        locationName: lodge.location.split('•')[0].trim(),
        statLine: parts.join(' | '),
        isBookable: false,
        comingSoonLabel: lodge.comingSoonLabel,
        lodge,
      });
    }

    return result;
  }, [allUnits, relevantLodges, unitsLoading, t]);

  // --- Carousel state ---
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false);
  const [isAtScrollStart, setIsAtScrollStart] = useState(true);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const recentlyAddedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Modal state ---
  const [bookingItem, setBookingItem] = useState<CrossSellCard | null>(null);
  const [infoItem, setInfoItem] = useState<CrossSellCard | null>(null);
  const [modalClosing, setModalClosing] = useState(false);

  // --- Booking modal state ---
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(2);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [pricePerNight, setPricePerNight] = useState<number | null>(null);
  const [cleaningFee, setCleaningFee] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Build set of unit IDs already in cart ---
  const addedItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of lodgeItems) {
      ids.add(item.itemId);
    }
    return ids;
  }, [lodgeItems]);

  // --- Cleanup timers ---
  useEffect(() => {
    return () => {
      if (recentlyAddedTimer.current) clearTimeout(recentlyAddedTimer.current);
    };
  }, []);

  // --- Body scroll lock when modal is open ---
  useEffect(() => {
    if ((bookingItem || infoItem) && !modalClosing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [bookingItem, infoItem, modalClosing]);

  // --- Guesty Quote hook (uses selected unit's guestyListingId) ---
  const handleQuoteReady = useCallback((quote: QuoteResponse) => {
    setEstimatedPrice(quote.pricing.total);
    setPricePerNight(quote.pricePerNight);
    setCleaningFee(quote.pricing.cleaning || 0);
    setPriceError(null);
  }, []);

  const handleQuoteExpired = useCallback(() => {
    setPriceError('Price quote expired. Please refresh.');
  }, []);

  const guestyQuote = useQuote({
    listingId: bookingItem?.unit?.guestyListingId || '',
    autoRefresh: true,
    refreshBuffer: 5,
    onQuoteReady: handleQuoteReady,
    onQuoteExpired: handleQuoteExpired,
  });

  // --- Max guests from unit data ---
  const maxGuests = bookingItem?.unit?.maxOccupancy || 8;

  // --- Calculate nights ---
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  // --- Price fetching (debounced) ---
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (!checkIn || !checkOut || !bookingItem?.unit) {
      setEstimatedPrice(null);
      setPricePerNight(null);
      setCleaningFee(null);
      setPriceLoading(false);
      return;
    }

    setPriceLoading(true);
    const unit = bookingItem.unit;
    const useGuestyAPI = Boolean(unit.guestyListingId);

    debounceTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setPriceError(null);

      // Try Guesty quote first
      if (useGuestyAPI && unit.guestyListingId) {
        if (controller.signal.aborted) return;
        const checkInStr = checkIn.toISOString().split('T')[0];
        const checkOutStr = checkOut.toISOString().split('T')[0];
        const quoteResult = await guestyQuote.fetchQuote(checkInStr, checkOutStr, guests);
        if (controller.signal.aborted) return;
        if (quoteResult) {
          setPriceLoading(false);
          return;
        }
      }

      // Fallback: availability API with unit ID
      if (controller.signal.aborted) return;
      try {
        const params = new URLSearchParams({
          startDate: checkIn.toISOString().split('T')[0],
          endDate: checkOut.toISOString().split('T')[0],
          guests: guests.toString(),
        });
        const apiBase = process.env.NEXT_PUBLIC_API_URL;
        const apiUrl = apiBase ? `${apiBase}/api` : '/api';
        const response = await fetch(`${apiUrl}/availability/lodge/${unit.id}?${params}`, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!response.ok) throw new Error('Failed to fetch pricing');
        const responseData = await response.json();
        if (controller.signal.aborted) return;

        if (!responseData?.data?.pricing) {
          setEstimatedPrice(null);
          setPricePerNight(null);
          setCleaningFee(null);
        } else {
          setEstimatedPrice(responseData.data.pricing.total);
          setPricePerNight(responseData.data.pricing.pricePerNight || null);
          setCleaningFee(responseData.data.pricing.breakdown?.cleaningFee || 0);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        setPriceError('Unable to calculate price.');
        setEstimatedPrice(null);
        setPricePerNight(null);
        setCleaningFee(null);
      } finally {
        if (!controller.signal.aborted) {
          setPriceLoading(false);
        }
      }
    }, PRICE_FETCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests, bookingItem?.unit?.id, bookingItem?.unit?.guestyListingId]);

  // --- Carousel scroll tracking ---
  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setNeedsScroll(el.scrollWidth > el.clientWidth + 10);
    const scrollLeft = el.scrollLeft;
    const cardEl = el.querySelector(`.${styles.lodgeCard}`) as HTMLElement | null;
    const cardWidth = cardEl?.clientWidth || 260;
    const gap = 16;
    const index = Math.round(scrollLeft / (cardWidth + gap));
    setActiveCardIndex(Math.min(index, items.length - 1));
    setIsAtScrollEnd(scrollLeft + el.clientWidth >= el.scrollWidth - 10);
    setIsAtScrollStart(scrollLeft <= 10);
  }, [items.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleCarouselScroll, { passive: true });
    handleCarouselScroll();
    const ro = new ResizeObserver(() => handleCarouselScroll());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', handleCarouselScroll);
      ro.disconnect();
    };
  }, [handleCarouselScroll]);

  const scrollToCard = useCallback((index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardEl = el.querySelector(`.${styles.lodgeCard}`) as HTMLElement | null;
    const cardWidth = cardEl?.clientWidth || 260;
    const gap = 16;
    el.scrollTo({ left: index * (cardWidth + gap), behavior: 'smooth' });
  }, []);

  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = 280 + 16;
    el.scrollBy({ left: direction === 'right' ? cardWidth : -cardWidth, behavior: 'smooth' });
  }, []);

  // --- Open booking modal ---
  const openBookingModal = useCallback((card: CrossSellCard) => {
    if (!card.unit) return;
    setBookingItem(card);
    setCheckIn(null);
    setCheckOut(null);
    setGuests(2);
    setEstimatedPrice(null);
    setPricePerNight(null);
    setCleaningFee(null);
    setPriceError(null);
    setShowSuccess(false);
    setModalClosing(false);
  }, []);

  // --- Open info modal ---
  const openInfoModal = useCallback((card: CrossSellCard) => {
    setInfoItem(card);
    setModalClosing(false);
  }, []);

  // --- Close any modal with animation ---
  const closeModal = useCallback(() => {
    setModalClosing(true);
    setTimeout(() => {
      setBookingItem(null);
      setInfoItem(null);
      setShowSuccess(false);
      setModalClosing(false);
    }, 250);
  }, []);

  // --- Handle date range selection ---
  const handleDateSelect = useCallback((range: { start: Date; end: Date } | null) => {
    if (range) {
      setCheckIn(range.start);
      setCheckOut(range.end);
      setPriceError(null);
    } else {
      setCheckIn(null);
      setCheckOut(null);
      setEstimatedPrice(null);
      setPricePerNight(null);
      setCleaningFee(null);
    }
  }, []);

  // --- Add to cart ---
  const handleAddToCart = useCallback(async () => {
    if (!checkIn || !checkOut || !bookingItem?.unit) return;

    const unit = bookingItem.unit;
    const useGuestyAPI = Boolean(unit.guestyListingId);

    try {
      let priceBreakdown;

      if (useGuestyAPI && guestyQuote.quote) {
        const quote = guestyQuote.quote;
        priceBreakdown = {
          basePrice: quote.pricing.accommodation,
          extraGuestFees: 0,
          cleaningFee: quote.pricing.cleaning,
          subtotal: quote.pricing.accommodation + quote.pricing.cleaning,
          promoDiscount: 0,
          groupDiscount: 0,
          totalDiscounts: 0,
          total: quote.pricing.total,
          currency: quote.pricing.currency,
          pricePerNight: quote.pricePerNight,
          nights,
        };
      } else {
        const params = new URLSearchParams({
          startDate: checkIn.toISOString().split('T')[0],
          endDate: checkOut.toISOString().split('T')[0],
          guests: guests.toString(),
        });
        const apiBase = process.env.NEXT_PUBLIC_API_URL;
        const apiUrl = apiBase ? `${apiBase}/api` : '/api';
        const response = await fetch(`${apiUrl}/availability/lodge/${unit.id}?${params}`);
        if (!response.ok) throw new Error('Failed to fetch pricing');
        const responseData = await response.json();
        const pricingData = responseData?.data?.pricing;
        if (!pricingData) throw new Error('Pricing not available');
        priceBreakdown = {
          basePrice: pricingData.breakdown?.basePrice || 0,
          extraGuestFees: pricingData.breakdown?.extraGuestFees || 0,
          cleaningFee: pricingData.breakdown?.cleaningFee || 0,
          subtotal: pricingData.breakdown?.subtotal || pricingData.total || 0,
          promoDiscount: 0,
          groupDiscount: 0,
          totalDiscounts: 0,
          total: pricingData.total || 0,
          currency: pricingData.currency || 'CLP',
          pricePerNight: pricingData.pricePerNight,
          nights,
        };
      }

      const checkInStr = checkIn.toISOString().split('T')[0];
      const checkOutStr = checkOut.toISOString().split('T')[0];

      addLodgeItem({
        itemType: 'lodge',
        itemId: unit.id,
        itemSlug: unit.id,
        itemName: unit.name,
        itemImage: unit.images?.[0] || bookingItem.lodge.heroImage,
        locationId: unit.locationId,
        checkIn: checkInStr,
        checkOut: checkOutStr,
        nights,
        guests,
        priceCLP: priceBreakdown.total,
        cleaningFeeCLP: priceBreakdown.cleaningFee || 0,
        nightlyRates: {},
        priceBreakdown,
        ...(guestyQuote.quote && { quoteId: guestyQuote.quote.quoteId }),
        ...(guestyQuote.quote && { ratePlanId: guestyQuote.quote.ratePlanId }),
        ...(guestyQuote.quote && {
          guestyPricing: {
            fareAccommodation: guestyQuote.quote.pricing.accommodation,
            fareCleaning: guestyQuote.quote.pricing.cleaning,
            totalTaxes: guestyQuote.quote.pricing.taxes,
            subTotalPrice: guestyQuote.quote.pricing.accommodation + guestyQuote.quote.pricing.cleaning,
            currency: guestyQuote.quote.pricing.currency,
          },
        }),
      });

      // Show success
      setShowSuccess(true);
      setRecentlyAdded(unit.id);
      if (recentlyAddedTimer.current) clearTimeout(recentlyAddedTimer.current);
      recentlyAddedTimer.current = setTimeout(() => setRecentlyAdded(null), 1200);

      if (useGuestyAPI) {
        guestyQuote.clearQuote();
      }

      // Auto-close after success animation
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (error) {
      console.error('Add to cart error:', error);
      setPriceError('Unable to add to cart. Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests, bookingItem, guestyQuote.quote, guestyQuote.clearQuote, addLodgeItem, nights, closeModal]);

  // --- Switch from info modal to booking modal ---
  const switchToBooking = useCallback((card: CrossSellCard) => {
    setInfoItem(null);
    setModalClosing(false);
    openBookingModal(card);
  }, [openBookingModal]);

  // --- Format CLP price ---
  const formatCLP = useCallback((amount: number) => {
    return `$${amount.toLocaleString('es-CL')}`;
  }, []);

  // --- No items or still loading ---
  if (relevantLodges.length === 0) {
    return null;
  }

  if (unitsLoading) {
    return (
      <div className={`${styles.wrapper} ${className || ''}`}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{t('title')}</h3>
        </div>
        <p className={styles.loadingMessage}>{t('loading')}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      {/* -- HEADER -- */}
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{t('title')}</h3>
      </div>
      <p className={styles.subtitle}>{t('subtitle')}</p>

      {/* -- CAROUSEL -- */}
      <div className={styles.carouselWrapper}>
        {needsScroll && (
          <button
            className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`}
            onClick={() => scrollCarousel('left')}
            aria-label={t('scrollLeft')}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className={styles.carousel} ref={carouselRef}>
          {items.map((card, idx) => {
            const isAdded = addedItemIds.has(card.id);
            const justAdded = recentlyAdded === card.id;

            return (
              <div
                key={card.id}
                className={`${styles.lodgeCard} ${isAdded ? styles.lodgeCardAdded : ''} ${justAdded ? styles.lodgeCardPop : ''}`}
                style={{ animationDelay: justAdded ? '0ms' : `${idx * 80 + 200}ms` }}
              >
                {/* Card image */}
                <div className={styles.cardImage}>
                  <Image
                    src={card.image}
                    alt={card.name}
                    fill
                    style={{ objectFit: 'cover' }}
                    sizes="(max-width: 640px) 72vw, 300px"
                    unoptimized
                  />
                  <div className={styles.cardImageOverlay} />
                  <span className={styles.locationBadge}>
                    <MapPinIcon />
                    {card.locationName}
                  </span>
                  {!card.isBookable && (
                    <div className={styles.comingSoonOverlay}>
                      <span className={styles.comingSoonBadge}>
                        {card.comingSoonLabel || t('comingSoon')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className={styles.cardBody}>
                  <h4 className={styles.cardTitle}>{card.name}</h4>
                  <div className={styles.statsRow}>
                    <span>{card.statLine}</span>
                  </div>

                  {/* Buttons */}
                  <div className={styles.cardButtons}>
                    <button
                      className={`${styles.reserveBtn} ${isAdded ? styles.reserveBtnAdded : ''} ${!card.isBookable ? styles.reserveBtnDisabled : ''}`}
                      onClick={() => card.isBookable && !isAdded && openBookingModal(card)}
                      disabled={!card.isBookable}
                    >
                      {isAdded ? (
                        <>
                          <CheckIcon size={14} />
                          <span>{t('reserved')}</span>
                        </>
                      ) : (
                        <span>{card.isBookable ? t('reserve') : t('comingSoon')}</span>
                      )}
                    </button>
                    <button
                      className={styles.infoBtn}
                      onClick={() => openInfoModal(card)}
                    >
                      {t('moreInfo')}
                    </button>
                  </div>
                </div>

                {isAdded && <div className={styles.addedGlow} />}
              </div>
            );
          })}
        </div>

        {needsScroll && (
          <button
            className={`${styles.carouselArrow} ${styles.carouselArrowRight}`}
            onClick={() => scrollCarousel('right')}
            aria-label={t('scrollRight')}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {needsScroll && (
          <>
            <div className={`${styles.carouselFadeLeft} ${isAtScrollStart ? styles.carouselFadeLeftHidden : ''}`} />
            <div className={`${styles.carouselFadeRight} ${isAtScrollEnd ? styles.carouselFadeRightHidden : ''}`} />
          </>
        )}
      </div>

      {/* -- DOT INDICATORS -- */}
      {needsScroll && (
        <div className={styles.carouselDots}>
          {items.map((card, i) => (
            <button
              key={card.id}
              className={`${styles.carouselDot} ${i === activeCardIndex ? styles.carouselDotActive : ''}`}
              onClick={() => scrollToCard(i)}
              aria-label={`Go to ${card.name}`}
            />
          ))}
        </div>
      )}

      {/* -- SKIP LINK -- */}
      {onSkip && (
        <div className={styles.skipRow}>
          <button className={styles.skipLink} onClick={onSkip}>
            {t('skip')}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}

      {/* ======================================================
          BOOKING MODAL (for real bookable units only)
          ====================================================== */}
      {bookingItem?.unit && (
        <div
          className={`${styles.modalBackdrop} ${modalClosing ? styles.modalBackdropClosing : ''}`}
          onClick={closeModal}
        >
          <div
            className={`${styles.bookingModal} ${modalClosing ? styles.bookingModalClosing : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.modalClose} onClick={closeModal} aria-label={t('close')}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>

            {/* Header */}
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderImage}>
                <Image
                  src={bookingItem.image}
                  alt={bookingItem.name}
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="460px"
                  unoptimized
                />
                <div className={styles.modalHeaderOverlay} />
                <div className={styles.modalHeaderInfo}>
                  <h3 className={styles.modalHeaderTitle}>{bookingItem.name}</h3>
                  <span className={styles.modalHeaderSub}>{bookingItem.lodge.location}</span>
                </div>
              </div>
            </div>

            {showSuccess ? (
              /* Success feedback */
              <div className={styles.successFeedback}>
                <div className={styles.successIcon}>
                  <CheckIcon size={24} />
                </div>
                <span className={styles.successText}>{t('addedToCart')}</span>
              </div>
            ) : (
              /* Booking body */
              <div className={styles.modalBody}>
                {/* Calendar */}
                <div className={styles.modalSection}>
                  <div className={styles.modalCalendar}>
                    <BookingCalendar
                      itemType="lodge"
                      itemId={bookingItem.unit.id}
                      locale={locale}
                      onRangeSelect={handleDateSelect}
                      guests={guests}
                      showPrices={false}
                      currency="CLP"
                      variant="flat"
                      hideHeader
                      hideLegend
                    />
                  </div>
                </div>

                {/* Guests */}
                <div className={styles.modalSection}>
                  <div className={styles.modalGuestsRow}>
                    <div className={styles.modalGuestsLabel}>
                      <span className={styles.modalGuestsTitle}>{t('guestsLabel')}</span>
                      <span className={styles.modalGuestsHint}>Max {maxGuests}</span>
                    </div>
                    <div className={styles.modalGuestsControls}>
                      <button
                        className={`${styles.modalGuestBtn} ${guests <= 1 ? styles.modalGuestBtnDisabled : ''}`}
                        onClick={() => setGuests(g => Math.max(1, g - 1))}
                        disabled={guests <= 1}
                        aria-label={t('decreaseGuests')}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14" strokeLinecap="round" />
                        </svg>
                      </button>
                      <span className={styles.modalGuestCount}>{guests}</span>
                      <button
                        className={`${styles.modalGuestBtn} ${guests >= maxGuests ? styles.modalGuestBtnDisabled : ''}`}
                        onClick={() => setGuests(g => Math.min(maxGuests, g + 1))}
                        disabled={guests >= maxGuests}
                        aria-label={t('increaseGuests')}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.modalDivider} />

                {/* Price display */}
                <div className={styles.modalPrice}>
                  {priceLoading ? (
                    <div className={styles.modalPriceLoading}>
                      <div className={styles.modalPriceSpinner} />
                      {t('loadingPrice')}
                    </div>
                  ) : estimatedPrice !== null && nights > 0 ? (
                    <>
                      {pricePerNight !== null && (
                        <div className={styles.modalPriceRow}>
                          <span className={styles.modalPriceLabel}>
                            {formatCLP(pricePerNight)} {t('perNight')} × {nights} {t('nights', { count: nights })}
                          </span>
                          <span className={styles.modalPriceValue}>{formatCLP(pricePerNight * nights)}</span>
                        </div>
                      )}
                      {cleaningFee !== null && cleaningFee > 0 && (
                        <div className={styles.modalPriceRow}>
                          <span className={styles.modalPriceLabel}>{t('cleaningFee')}</span>
                          <span className={styles.modalPriceValue}>{formatCLP(cleaningFee)}</span>
                        </div>
                      )}
                      <div className={styles.modalPriceTotal}>
                        <span className={styles.modalPriceTotalLabel}>{t('total')}</span>
                        <span className={styles.modalPriceTotalValue}>{formatCLP(estimatedPrice)}</span>
                      </div>
                    </>
                  ) : priceError ? (
                    <span className={styles.modalPriceEmpty}>{priceError}</span>
                  ) : (
                    <span className={styles.modalPriceEmpty}>{t('selectDatesToAdd')}</span>
                  )}
                </div>

                {/* Add to cart button */}
                <button
                  className={`${styles.modalConfirmBtn} ${(!estimatedPrice || priceLoading) ? styles.modalConfirmBtnDisabled : ''}`}
                  onClick={handleAddToCart}
                  disabled={!estimatedPrice || priceLoading}
                >
                  <span>
                    {estimatedPrice ? `${t('addToCart')} — ${formatCLP(estimatedPrice)}` : t('selectDatesToAdd')}
                  </span>
                  {estimatedPrice && !priceLoading && (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================================================
          INFO MODAL (for units or comingSoon lodges)
          ====================================================== */}
      {infoItem && (
        <div
          className={`${styles.modalBackdrop} ${modalClosing ? styles.modalBackdropClosing : ''}`}
          onClick={closeModal}
        >
          <div
            className={`${styles.infoModal} ${modalClosing ? styles.infoModalClosing : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.modalClose} onClick={closeModal} aria-label={t('close')}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>

            {/* Gallery - use unit images or lodge gallery */}
            <div className={styles.infoGallery}>
              {(infoItem.unit?.images || infoItem.lodge.gallery).slice(0, 5).map((img, i) => (
                <div key={i} className={styles.infoGallerySlide}>
                  <Image
                    src={img}
                    alt={
                      infoItem.unit
                        ? (infoItem.unit.imageAlts?.[i] || infoItem.name)
                        : (infoItem.lodge.galleryAlts[i] || infoItem.name)
                    }
                    fill
                    style={{ objectFit: 'cover' }}
                    sizes="520px"
                    unoptimized
                  />
                </div>
              ))}
            </div>

            {/* Body */}
            <div className={styles.infoBody}>
              <h3 className={styles.infoName}>{infoItem.name}</h3>
              <p className={styles.infoSubtitle}>
                {infoItem.unit ? infoItem.unit.bedConfiguration : infoItem.lodge.subtitle}
              </p>
              <div className={styles.infoLocation}>
                <MapPinIcon />
                {infoItem.lodge.location}
              </div>

              {/* Stats grid */}
              <div className={styles.infoStatsGrid}>
                {infoItem.unit ? (
                  <>
                    <div className={styles.infoStat}>
                      <span className={styles.infoStatNumber}>{infoItem.unit.maxOccupancy}</span>
                      <span className={styles.infoStatLabel}>{t('guests')}</span>
                    </div>
                    {infoItem.unit.bedrooms && (
                      <div className={styles.infoStat}>
                        <span className={styles.infoStatNumber}>{infoItem.unit.bedrooms}</span>
                        <span className={styles.infoStatLabel}>{t('bedrooms')}</span>
                      </div>
                    )}
                    {infoItem.unit.bathroomCount > 0 && (
                      <div className={styles.infoStat}>
                        <span className={styles.infoStatNumber}>{infoItem.unit.bathroomCount}</span>
                        <span className={styles.infoStatLabel}>{t('bathrooms')}</span>
                      </div>
                    )}
                    <div className={styles.infoStat}>
                      <span className={styles.infoStatNumber}>{infoItem.unit.pricing.minimumNights}</span>
                      <span className={styles.infoStatLabel}>{t('minNights')}</span>
                    </div>
                  </>
                ) : (
                  infoItem.lodge.stats.slice(0, 4).map((stat, i) => (
                    <div key={i} className={styles.infoStat}>
                      <span className={styles.infoStatNumber}>{stat.number}</span>
                      <span className={styles.infoStatLabel}>{stat.label}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Description */}
              <p className={styles.infoDescription}>
                {infoItem.unit
                  ? (infoItem.unit.description || infoItem.unit.spaceDescription || infoItem.lodge.detailsDescription)
                  : infoItem.lodge.detailsDescription
                }
              </p>

              {/* Amenities (for units) */}
              {infoItem.unit?.amenities && infoItem.unit.amenities.length > 0 && (
                <>
                  <h4 className={styles.infoListTitle}>{t('amenities')}</h4>
                  <ul className={styles.infoList}>
                    {infoItem.unit.amenities.slice(0, 8).map((amenity, i) => (
                      <li key={i}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {amenity}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Special Features (for comingSoon lodges) */}
              {!infoItem.unit && infoItem.lodge.specialFeatures.length > 0 && (
                <>
                  <h4 className={styles.infoListTitle}>{t('specialFeatures')}</h4>
                  <ul className={styles.infoList}>
                    {infoItem.lodge.specialFeatures.map((feature, i) => (
                      <li key={i}>
                        <StarIcon />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Facilities (for comingSoon lodges) */}
              {!infoItem.unit && infoItem.lodge.facilities.length > 0 && (
                <>
                  <h4 className={styles.infoListTitle}>{t('facilities')}</h4>
                  <ul className={styles.infoList}>
                    {infoItem.lodge.facilities.map((facility, i) => (
                      <li key={i}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {facility}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* CTA */}
              <button
                className={`${styles.infoCta} ${!infoItem.isBookable ? styles.infoCtaDisabled : ''}`}
                onClick={() => infoItem.isBookable && switchToBooking(infoItem)}
                disabled={!infoItem.isBookable}
              >
                {infoItem.isBookable ? t('reserveNow') : (infoItem.comingSoonLabel || t('comingSoon'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
